import { useNavigationPresentation, TRADE_WORKSPACE } from "@/lib/tradeNavigation";
// Workspace switcher — primary nav control to move between Fieldlink workspaces
// (Civil Operations / Supply Yard). Same session, tenant, users and data.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { WORKSPACES, availableWorkspaces } from "@/constants/nav";

const LAST_KEY = "fl_ws_last";
const readLast = () => { try { return JSON.parse(localStorage.getItem(LAST_KEY) || "{}"); } catch { return {}; } };
export const rememberWorkspaceRoute = (workspace, pathname) => {
  try { const m = readLast(); m[workspace] = pathname; localStorage.setItem(LAST_KEY, JSON.stringify(m)); } catch { /* ignore */ }
};

export function WorkspaceSwitcher({ current, user, expanded = true, variant = "desktop", onNavigate }) {
  const { trades } = useNavigationPresentation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const options = trades ? [TRADE_WORKSPACE] : availableWorkspaces(user);
  const active = trades ? TRADE_WORKSPACE : WORKSPACES.find((w) => w.id === current) || WORKSPACES[0];
  const ActiveIcon = active.icon;

  // Only one workspace available → show a static label, no switcher.
  if (options.length < 2) {
    if (!expanded) {
      return (
        <div className="fl-workspace-control w-11 h-11 mx-auto rounded-lg bg-white/[0.04] border fl-nav-border border-white/10 grid place-items-center text-[#FF5F15]" title={active.label} data-testid="workspace-current-icon">
          <ActiveIcon className="w-[18px] h-[18px]" strokeWidth={2} />
        </div>
      );
    }
    return (
      <div className="fl-workspace-control flex items-center gap-2.5 rounded-lg bg-white/[0.04] border fl-nav-border border-white/10 px-3 h-11" data-testid="workspace-current">
        <ActiveIcon className="w-[18px] h-[18px] text-[#FF5F15] shrink-0" strokeWidth={2} />
        <span className="font-display font-bold text-[13px] uppercase tracking-wide fl-nav-primary text-white truncate">{active.label}</span>
      </div>
    );
  }

  const choose = (w) => {
    if (w.id !== current) {
      const last = readLast()[w.id];
      nav(last || w.home);
    }
    setOpen(false);
    onNavigate?.();
  };

  const trigger = !expanded ? (
    <button className="fl-workspace-control w-11 h-11 mx-auto rounded-lg bg-white/[0.04] border border-[#FF5F15]/40 grid place-items-center text-[#FF5F15] hover:bg-white/[0.08] transition-colors" title={`Workspace: ${active.label}`} data-testid="workspace-switcher-trigger">
      <ActiveIcon className="w-[18px] h-[18px]" strokeWidth={2} />
    </button>
  ) : (
    <button className="fl-workspace-control w-full flex items-center gap-2.5 rounded-lg bg-white/[0.04] border border-[#FF5F15]/45 px-3 h-11 hover:bg-white/[0.08] transition-colors group" data-testid="workspace-switcher-trigger">
      <ActiveIcon className="w-[18px] h-[18px] text-[#FF5F15] shrink-0" strokeWidth={2} />
      <span className="flex-1 min-w-0 text-left">
        <span className="block font-display font-bold text-[13px] uppercase tracking-wide fl-nav-primary text-white leading-none truncate">{active.label}</span>
        <span className="block text-[11px] font-bold uppercase tracking-[0.18em] fl-nav-muted text-[#7B8798] mt-1">Workspace</span>
      </span>
      <ChevronsUpDown className="w-3.5 h-3.5 fl-nav-muted text-[#7B8798] group-hover:text-white shrink-0" strokeWidth={2.5} />
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" side={variant === "desktop" && !expanded ? "right" : "bottom"}
        className="fl-nav-theme fl-nav-flyout w-[264px] p-1.5 bg-[#0D141C] border fl-nav-border border-white/10 fl-nav-primary text-white rounded-lg shadow-xl" data-testid="workspace-switcher-panel">
        <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] fl-nav-muted text-[#7B8798]">Switch workspace</div>
        <div className="space-y-0.5">
          {options.map((w) => {
            const Icon = w.icon;
            const on = w.id === current;
            return (
              <button key={w.id} onClick={() => choose(w)} data-testid={`workspace-option-${w.id}`}
                className={`w-full flex items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${on ? "fl-workspace-selected bg-[#FF5F15]/12 border border-[#FF5F15]/40" : "fl-nav-idle border border-transparent hover:bg-white/[0.06]"}`}>
                <Icon className={`w-[18px] h-[18px] shrink-0 mt-0.5 ${on ? "text-[#FF5F15]" : "fl-nav-primary text-[#C6CEDA]"}`} strokeWidth={2} />
                <span className="flex-1 min-w-0">
                  <span className={`flex items-center gap-1.5 font-display font-bold text-[13px] uppercase tracking-wide ${on ? "fl-nav-primary text-white" : "fl-nav-primary text-[#C6CEDA]"}`}>
                    {w.label}
                    {on && <Check className="w-3.5 h-3.5 text-[#FF5F15]" strokeWidth={3} />}
                  </span>
                  <span className="block text-[11px] fl-nav-muted text-[#7B8798] leading-snug mt-0.5">{w.desc}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
